import { useCallback } from 'react'
import { getCachedSpaceDataForAnalytics } from '@components/Analytics/useGatherSpaceDetailsAnalytics'
import { useJoinFunnelAnalytics } from '@components/Analytics/useJoinFunnelAnalytics'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { mapToErrorMessage } from '@components/Web3/utils'
import { useGetSignerWithTimeout } from '@towns/privy'
import { useEnvironment } from 'hooks/useEnvironment'
import { useMyUserId, useTownsClient, useTownsContext } from 'use-towns-client'
import { useShallow } from 'zustand/react/shallow'

import { publicPageLoginStore } from '../usePublicPageLoginFlow'

import { useInstallSubscriptionAfterJoin } from './useInstallSubscriptionAfterJoin'

type TClientSingleton = ReturnType<typeof useTownsClient>['clientSingleton']
type TSignerContext = ReturnType<typeof useTownsContext>['signerContext']

export function useJoinAfterSuccessfulCrossmint({ spaceId }: { spaceId: string | undefined }) {
    const { joiningTown, joinedTown } = useJoinFunnelAnalytics()
    const userId = useMyUserId()
    const { baseChain } = useEnvironment()
    const getSigner = useGetSignerWithTimeout({ chainId: baseChain.id })
    const installSubscriptionAfterJoin = useInstallSubscriptionAfterJoin({ spaceId })

    const { setJoinStatusStep, endPublicPageLoginFlow } = publicPageLoginStore(
        useShallow((s) => ({
            setJoinStatusStep: s.setJoinStatusStep,
            endPublicPageLoginFlow: s.endPublicPageLoginFlow,
        })),
    )
    return useCallback(
        async ({
            clientSingleton,
            signerContext,
            analyticsData,
        }: {
            clientSingleton: TClientSingleton
            signerContext: TSignerContext
            analyticsData: {
                spaceName: string
            }
        }) => {
            if (!spaceId || !clientSingleton || !signerContext) {
                const missingParams: string[] = []
                if (!spaceId) {
                    missingParams.push('spaceId')
                }
                if (!clientSingleton) {
                    missingParams.push('clientSingleton')
                }
                if (!signerContext) {
                    missingParams.push('signerContext')
                }
                popupToast(({ toast }) => (
                    <StandardToast.Error
                        toast={toast}
                        message="There was an error joining the town."
                        subMessage={mapToErrorMessage({
                            error: new Error(
                                `[usePublicPageLoginFlow] missing params: ${missingParams.join(
                                    ', ',
                                )} - crossmintSuccess`,
                            ),
                            source: 'crossmintSuccess',
                        })}
                    />
                ))
                endPublicPageLoginFlow()
                return
            }
            joiningTown({ spaceId, spaceName: analyticsData.spaceName })

            try {
                const result = await clientSingleton?.joinRiverSpaceAndDefaultChannels(
                    spaceId,
                    signerContext,
                    (status) => setJoinStatusStep(status),
                    undefined,
                )

                if (result) {
                    const { gatedSpace, priceInWei, pricingModule, tokensGatedBy, duration } =
                        getCachedSpaceDataForAnalytics()
                    joinedTown({
                        spaceId,
                        spaceName: analyticsData.spaceName,
                        gatedSpace,
                        pricingModule,
                        priceInWei,
                        tokensGatedBy,
                        duration,
                    })
                    if (userId) {
                        getSigner()
                            .then((signer) => {
                                if (signer) {
                                    installSubscriptionAfterJoin({ userId, signer })
                                }
                            })
                            .catch((error) => {
                                console.error(
                                    'Error installing subscription after joining with crossmint',
                                    error,
                                )
                            })
                    }
                } else {
                    endPublicPageLoginFlow()
                    popupToast(({ toast }) => (
                        <StandardToast.Error
                            toast={toast}
                            message="There was an error joining the town."
                            subMessage={mapToErrorMessage({
                                error: new Error('unable to join space unknown'),
                                source: 'join space',
                            })}
                        />
                    ))
                }
            } catch (error) {
                endPublicPageLoginFlow()
                const _error = error as Error

                popupToast(({ toast }) => (
                    <StandardToast.Error
                        toast={toast}
                        message="There was an error joining the town."
                        subMessage={mapToErrorMessage({ error: _error, source: 'join space' })}
                    />
                ))
            }
        },
        [
            endPublicPageLoginFlow,
            getSigner,
            installSubscriptionAfterJoin,
            joinedTown,
            joiningTown,
            setJoinStatusStep,
            spaceId,
            userId,
        ],
    )
}
